import React from 'react'
import { Http, Vault } from 'lib'
import IApiResponse from '../../types/IApiResponse'
import IUserData from '../../types/sign-in/IUserData'
import ISignInRequestPayload from '../../types/sign-in/ISignInRequestPayload'
import OnSuccessDelegate from '../../types/OnSuccessDelegate'
import OnErrorDelegate from '../../types/OnErrorDelegate'

import { SignInState } from './store/State'

import {
  setEmail,
  setPassword,
  setIsValidEmail,
  setIsSubmitting,
  setError,
  setIsRequestResetPasswordDialogOpen,
  setIsSendingResetPasswordRequest,
  setRequestResetPasswordEmail,
  setIsRequestResetPasswordEmailValid,
  SignInAction
} from './store/Actions'

import apiUrlsAddresses from '../../config/constants/api-urls-addresses'

export default class SignInService {
  static isValidEmail(email: string): boolean {
    return Boolean(email) && /^\w+.+@[^.].*\.[a-z]{2,}$/.test(email)
  }

  static isValidPassword(password: string): boolean {
    return Boolean(password) && /^(?=.*?[A-Z])(?=.*?[0-9]).{6,}$/.test(password)
  }

  static isValidSubmission(email: string): [] | [string, string] {
    if (!SignInService.isValidEmail(email)) {
      return ['email', 'invalid email']
    }

    return []
  }

  static onEmailChange(state: SignInState, dispatch: React.Dispatch<SignInAction>) {
    return (email: string) => {
      if (email !== state.email) {
        dispatch(setEmail(email))
      }
    }
  }

  static onEmailTouched(state: SignInState, dispatch: React.Dispatch<SignInAction>) {
    return () => {
      dispatch(setIsValidEmail(SignInService.isValidEmail(state.email)))
    }
  }

  static onPasswordChanged(state: SignInState, dispatch: React.Dispatch<SignInAction>) {
    return (password: string) => {
      if (password !== state.password) {
        dispatch(setPassword(password))
      }
    }
  }

  static async signIn(
    payload: ISignInRequestPayload
  ): Promise<[IApiResponse<IUserData> | null, number, string | null]> {
    return Http.post<ISignInRequestPayload, IApiResponse<IUserData>>(apiUrlsAddresses.session.signIn(), payload)
  }

  static onSubmit(
    state: SignInState,
    dispatch: React.Dispatch<SignInAction>,
    onSuccess: OnSuccessDelegate<IApiResponse<IUserData>>,
    onError: OnErrorDelegate,
    enterpriseId?: string,
    submissionLinkCode?: string
  ) {
    return async () => {
      const [, error] = SignInService.isValidSubmission(state.email)

      if (error) {
        dispatch(setError(error as string))
        dispatch(setIsValidEmail(false))
        return
      }

      dispatch(setIsSubmitting(true))
      dispatch(setIsValidEmail(true))
      dispatch(setError(''))

      const [response, , requestError] = await SignInService.signIn({
        email: state.email,
        password: state.password,
        ...(enterpriseId && submissionLinkCode
          ? {
              enterpriseId,
              submissionLinkCode
            }
          : {})
      })

      if (Boolean(requestError)) {
        onError(requestError as string)
      } else {
        const signInResponse = response as IApiResponse<IUserData>
        Vault.setAccessToken(signInResponse.data.token as string)
        onSuccess(signInResponse)
      }

      dispatch(setIsSubmitting(false))
    }
  }

  static toggleResetPasswordDialog(state: SignInState, dispatch: React.Dispatch<SignInAction>) {
    return () => {
      dispatch(setIsRequestResetPasswordDialogOpen(!state.isRequestResetPasswordDialogOpen))
    }
  }

  static setResetPasswordEmail(state: SignInState, dispatch: React.Dispatch<SignInAction>) {
    return (email: string) => {
      if (state.requestResetPasswordEmail !== email) {
        dispatch(setRequestResetPasswordEmail(email))
      }
    }
  }

  static isValidRequestResetPassword(state: SignInState): boolean {
    return SignInService.isValidEmail(state.requestResetPasswordEmail)
  }

  static validateResetPasswordEmail(state: SignInState, dispatch: React.Dispatch<SignInAction>) {
    return () => {
      dispatch(setIsRequestResetPasswordEmailValid(SignInService.isValidRequestResetPassword(state)))
    }
  }

  static requestResetPassword(
    state: SignInState,
    dispatch: React.Dispatch<SignInAction>,
    onSuccess: () => void,
    onError: (error: string) => void
  ) {
    return async () => {
      if (state.isSendingResetPasswordRequest) {
        return
      }

      if (!SignInService.isValidRequestResetPassword(state)) {
        onError('Invalid email')
        return
      }

      dispatch(setIsSendingResetPasswordRequest(true))

      const [, , error] = await Http.post<{ email: string }, { sent: true }>(apiUrlsAddresses.account.resetPassword(), {
        email: state.requestResetPasswordEmail
      })

      dispatch(setIsSendingResetPasswordRequest(false))

      if (error) {
        onError(error)
        return
      }

      onSuccess()

      SignInService.resetRequestResetPasswordState(state, dispatch)()
    }
  }

  static resetRequestResetPasswordState(_state: SignInState, dispatch: React.Dispatch<SignInAction>) {
    return () => {
      dispatch(setIsRequestResetPasswordDialogOpen(false))
      dispatch(setIsSendingResetPasswordRequest(false))
      dispatch(setRequestResetPasswordEmail(''))
      dispatch(setIsRequestResetPasswordEmailValid(true))
    }
  }
}
